import { toValidPromptContexts, toValidPromptRecords } from "~/notebook/core/prompt-provenance";
import {
  type CellLink,
  type CellPromptRecord,
  type LinkTargetKind,
  type Notebook,
  type NotebookCell,
} from "~/notebook/model/model";

const LINK_TARGET_KINDS: ReadonlySet<LinkTargetKind> = new Set(["model", "artifact", "system"]);

// `.ipynb` content is attacker-controllable (any file in S3), so links read from cell metadata are
// validated rather than trusted. Malformed entries are dropped instead of flowing into navigation
// hrefs and kernel bindings as arbitrary objects.
function toValidCellLinks(value: unknown): CellLink[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is CellLink => {
    if (!item || typeof item !== "object") return false;
    const link = item as Record<string, unknown>;
    return (
      typeof link.id === "string" &&
      typeof link.alias === "string" &&
      typeof link.targetId === "string" &&
      typeof link.targetDisplayName === "string" &&
      typeof link.targetKind === "string" &&
      LINK_TARGET_KINDS.has(link.targetKind as LinkTargetKind) &&
      (link.targetRevisionId === undefined || typeof link.targetRevisionId === "string") &&
      (link.targetExtension === undefined || typeof link.targetExtension === "string")
    );
  });
}

type IpynbCellMetadata = {
  "istari.links"?: CellLink[];
  "istari.id"?: string;
  "istari.prompts"?: CellPromptRecord[];
};

type IpynbCell = {
  cell_type: "code" | "markdown" | "raw";
  metadata: IpynbCellMetadata;
  source: string[] | string;
  execution_count?: number | null;
  outputs?: unknown[];
};

type IpynbDoc = {
  nbformat: 4;
  nbformat_minor: number;
  metadata: {
    kernelspec?: { name: string; display_name: string };
    language_info?: { name: string };
    istari?: {
      notebookId?: string;
      displayName?: string;
      revisionLabel?: string;
      promptContexts?: Record<string, string>;
    };
  };
  cells: IpynbCell[];
};

function splitLines(source: string): string[] {
  if (source === "") return [];
  const lines = source.split("\n");
  return lines.map((line, i) => (i === lines.length - 1 ? line : line + "\n"));
}

function joinLines(source: unknown): string {
  if (Array.isArray(source)) return source.map((line) => (typeof line === "string" ? line : "")).join("");
  if (typeof source === "string") return source;
  return "";
}

export function toIpynb(notebook: Notebook): IpynbDoc {
  const referenced = new Set(notebook.cells.flatMap((cell) => cell.prompts ?? []).map((record) => record.contextKey));
  const promptContexts = Object.fromEntries(
    Object.entries(notebook.promptContexts ?? {}).filter(([key]) => referenced.has(key)),
  );
  return {
    nbformat: 4,
    nbformat_minor: 5,
    metadata: {
      kernelspec: { name: "python", display_name: "Python (Pyodide)" },
      language_info: { name: "python" },
      istari: {
        notebookId: notebook.resourceId,
        displayName: notebook.displayName,
        revisionLabel: notebook.revisionLabel,
        ...(Object.keys(promptContexts).length > 0 ? { promptContexts } : {}),
      },
    },
    cells: notebook.cells.map((cell) => ({
      cell_type: cell.kind === "markdown" ? "markdown" : "code",
      // Persist the cell id so reopening/refetching keeps stable ids (selection, links, React keys).
      metadata: {
        "istari.id": cell.id,
        ...(cell.links.length > 0 ? { "istari.links": cell.links } : {}),
        ...(cell.prompts && cell.prompts.length > 0 ? { "istari.prompts": cell.prompts } : {}),
      },
      source: splitLines(cell.source),
      ...(cell.kind === "code" ? { execution_count: cell.executionCount ?? null, outputs: [] } : {}),
    })),
  };
}

// Cell outputs are NEVER persisted: toIpynb writes `outputs: []` and fromIpynb does not read the
// `outputs` field below. This is the Jupyter convention for source-controlled notebooks and is also
// our XSS mitigation — rich-mime outputs are rendered via dangerouslySetInnerHTML, so loading them
// from an attacker-controlled .ipynb in S3 would otherwise be a stored XSS vector.
export function fromIpynb(doc: unknown, fallbackId: string, fallbackName: string): Notebook | null {
  if (!doc || typeof doc !== "object") return null;
  const raw = doc as Partial<IpynbDoc>;
  if (!Array.isArray(raw.cells)) return null;

  const istariMeta = raw.metadata?.istari ?? {};
  const cells: NotebookCell[] = raw.cells.map((cell, index) => {
    const source = joinLines(cell.source);
    const links = toValidCellLinks(cell.metadata?.["istari.links"]);
    const kind: NotebookCell["kind"] = cell.cell_type === "markdown" ? "markdown" : "code";
    const persistedId = typeof cell.metadata?.["istari.id"] === "string" ? cell.metadata["istari.id"] : undefined;
    const prompts = toValidPromptRecords(cell.metadata?.["istari.prompts"]);
    return {
      // Prefer the persisted id; the index fallback is still stable across re-parses of the same doc.
      id: persistedId ?? `cell-${index}`,
      kind,
      source,
      links,
      executionCount: typeof cell.execution_count === "number" ? cell.execution_count : undefined,
      ...(prompts.length > 0 ? { prompts } : {}),
    };
  });

  return {
    // Use `||` not `??`: blank notebooks persist `notebookId: ""`, which must fall back to the real id.
    resourceId: istariMeta.notebookId || fallbackId,
    displayName: istariMeta.displayName || fallbackName,
    extension: "ipynb",
    revisionLabel: istariMeta.revisionLabel ?? "v1",
    cells,
    createdAt: new Date().toISOString(),
    promptContexts: toValidPromptContexts(istariMeta.promptContexts),
  };
}

export function downloadAsIpynb(notebook: Notebook): void {
  const doc = toIpynb(notebook);
  const blob = new Blob([JSON.stringify(doc, null, 1)], { type: "application/x-ipynb+json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const filename = notebook.displayName.endsWith(".ipynb") ? notebook.displayName : `${notebook.displayName}.ipynb`;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
